import type {
  ActionItem,
  ActionItemStatus,
  DayFocus,
  TaskPlan,
  TodoProject,
  TodoProjectColorKey,
  TodoSwimLane,
} from '../types';

export const TODO_SWIM_LANE_META: Array<{ id: TodoSwimLane; label: string; hint: string }> = [
  { id: 'today', label: 'Today', hint: 'Aiming to touch this today' },
  { id: 'this_week', label: 'This week', hint: 'Plan to finish this calendar week' },
  { id: 'later', label: 'Later', hint: 'Backlog — no commitment yet' },
];

/** Lane-keyed copy for cascade dialogs and toasts. */
export const TODO_LANE_LABELS: Record<TodoSwimLane, string> = {
  today: 'Today',
  this_week: 'This week',
  later: 'Later',
};

export const TODO_PROJECT_COLOR_KEYS: TodoProjectColorKey[] = [
  'teal',
  'midnight',
  'sun',
  'plum',
  'ocean',
  'sage',
];

/**
 * Maps each project colour slug to a CSS-resolvable token + soft tint.
 * Render-time only — Firestore stores the slug string.
 *
 * Tokens deliberately point at CSS custom properties (defined in
 * `src/index.css`) so they auto-shift between light and dark mode without
 * runtime branching. The `.dark` overrides lift lightness so colored pills /
 * chips stay readable against the midnight-700 background — previously plum
 * and midnight nearly vanished.
 */
export const TODO_PROJECT_COLOR_TOKENS: Record<
  TodoProjectColorKey,
  { fg: string; tint: string }
> = {
  teal: { fg: 'var(--proj-teal-fg)', tint: 'var(--proj-teal-tint)' },
  midnight: { fg: 'var(--proj-midnight-fg)', tint: 'var(--proj-midnight-tint)' },
  sun: { fg: 'var(--proj-sun-fg)', tint: 'var(--proj-sun-tint)' },
  plum: { fg: 'var(--proj-plum-fg)', tint: 'var(--proj-plum-tint)' },
  ocean: { fg: 'var(--proj-ocean-fg)', tint: 'var(--proj-ocean-tint)' },
  sage: { fg: 'var(--proj-sage-fg)', tint: 'var(--proj-sage-tint)' },
};

export function parseLocalDateKey(dateKey: string): Date {
  const [y, m, d] = dateKey.split('-').map((p) => Number(p));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return new Date(NaN);
  return new Date(y, m - 1, d);
}

/** Monday-start week in local timezone (Monday … Sunday). */
export function startOfWeekMondayLocal(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function isDateKeyInCalendarWeek(dateKey: string, todayKey: string): boolean {
  const fd = parseLocalDateKey(dateKey);
  const today = parseLocalDateKey(todayKey);
  if (Number.isNaN(fd.getTime()) || Number.isNaN(today.getTime())) return false;
  const ws = startOfWeekMondayLocal(today);
  const we = new Date(ws);
  we.setDate(we.getDate() + 7);
  return fd >= ws && fd < we;
}

export function deriveTodoLaneFromDayFocusDates(focus: DayFocus, todayKey: string): TodoSwimLane {
  if (focus.focusDate === todayKey) return 'today';
  if (isDateKeyInCalendarWeek(focus.focusDate, todayKey)) return 'this_week';
  return 'later';
}

/** Persisted `swimLane` wins when set; otherwise derive from dates for Day Focuses. */
export function effectiveSwimLaneDayFocus(focus: DayFocus, todayKey: string): TodoSwimLane {
  return focus.swimLane ?? deriveTodoLaneFromDayFocusDates(focus, todayKey);
}

export function effectiveSwimLaneTaskPlan(task: TaskPlan): TodoSwimLane {
  return task.swimLane ?? 'later';
}

function actionItemStatus(item: ActionItem): ActionItemStatus {
  if (item.status) return item.status;
  return item.completed ? 'done' : 'todo';
}

/**
 * Workspace Kanban column for a whole card (tie-break: leftmost workflow column with open work).
 */
export function workspaceKanbanColumnForTask(task: TaskPlan): ActionItemStatus {
  const items = task.actionItems;
  if (items.length === 0) return 'todo';
  if (items.every((i) => actionItemStatus(i) === 'done')) return 'done';
  if (items.some((i) => actionItemStatus(i) === 'todo')) return 'todo';
  if (items.some((i) => actionItemStatus(i) === 'in_progress')) return 'in_progress';
  return 'todo';
}

export function taskHasMixedOpenStatuses(task: TaskPlan): boolean {
  const items = task.actionItems.filter((i) => actionItemStatus(i) !== 'done');
  if (items.length <= 1) return false;
  const first = actionItemStatus(items[0]);
  return items.some((i) => actionItemStatus(i) !== first);
}

/**
 * Whole-list completeness predicate, lifted from `TodosPage` so cascade and
 * unit tests can share a single source of truth (`todos-project-pivot-prd.md` §7.2).
 */
export function isTaskListFullyCompleted(task: TaskPlan): boolean {
  if (task.status === 'completed') return true;
  if (task.actionItems.length === 0) return false;
  return task.actionItems.every((item) => actionItemStatus(item) === 'done');
}

export function isFocusFullyCompleted(focus: DayFocus): boolean {
  if (focus.status === 'completed') return true;
  if (focus.actionItems.length === 0) return false;
  return focus.actionItems.every((item) => actionItemStatus(item) === 'done');
}

/** Strict `YYYY-MM-DD` validator; rejects malformed Firestore writes client-side. */
const ISO_DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export function isValidDateKey(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = ISO_DATE_KEY_RE.exec(value);
  if (!match) return false;
  const [, y, m, d] = match;
  const month = Number(m);
  const day = Number(d);
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  const probe = new Date(Number(y), month - 1, day);
  return (
    probe.getFullYear() === Number(y)
    && probe.getMonth() === month - 1
    && probe.getDate() === day
  );
}

/** Sort projects: pinned first, then by name (case-insensitive), archived hidden by default. */
export function sortProjectsForPivot(projects: TodoProject[], options?: { includeArchived?: boolean }): TodoProject[] {
  const includeArchived = Boolean(options?.includeArchived);
  return projects
    .filter((p) => includeArchived || !p.archived)
    .slice()
    .sort((a, b) => {
      const ap = a.pinned ? 0 : 1;
      const bp = b.pinned ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
}
